import React, { useEffect, useCallback } from 'react';
import {
    Accordion,
    AccordionActions,
    AccordionDetails,
    AccordionSummary,
    Box,
    Stack,
    Grid,
    Button,
    Chip,
    Fade,
    Tooltip,
    Typography,
    Badge,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Form, reduxForm } from 'redux-form';
import uuid from 'uuid/v4';
import { withTranslation } from 'react-i18next';

import FilterViewMapper from './FilterViewMapper';
import onSubmit from './onSubmit';
import validate from './validate';

/* ================= styles ================= */
const FILTER_FORM_NAME = `filter-${uuid()}`;

const RootAccordion = styled(Accordion)(({ theme }) => ({
    // width: '100%',
    // boxShadow: 'none',
    // backgroundColor: theme.palette.grey[100],
    // '&:before': {
    //     opacity: 0,
    // },
}));

const Heading = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.pxToRem(15),
}));

const Details = styled(AccordionDetails)({
    // padding: 0,
});

const ListGrid = styled(Grid)(({ theme }) => ({
    // width: '100%',
    // marginLeft: theme.spacing(2),
    // marginRight: theme.spacing(2),
    // marginTop: 0,
    // marginBottom: 0,
}));

/* ================= component ================= */

function View(props) {
    const {
        expanded,
        handleSubmit,
        filter,
        onAccept,
        expandFilterView,
        rollUpFilterView,
        model,
        fetchOnFilter,
        initialize,
        reset,
        setFilterValues,
        t,
    } = props;

    useEffect(() => {
        initialize(filter.reduce((acc, { values, code }) => (!values ? acc : { ...acc, [code]: values }), {}));
    }, [filter, initialize]);

    const handleAddFilter = useCallback(
        data => {
            setFilterValues(data);
        },
        [setFilterValues]
    );

    const hiddenCodes = [
        'messageFiles',
        'created_at',
        'editSettingsVillage',
        'wolking_time',
        'quantity_tasks',
        'createdMoneyBoxTime',
        'amount',
    ];

    return (
        <Form name="filter_form" onSubmit={handleSubmit}>
            <RootAccordion
                expanded={expanded}
                onChange={expanded ? rollUpFilterView : expandFilterView}
                className="filter_form"
            >
                <AccordionSummary
                    expandIcon={
                        <Tooltip placement="top" title={expanded ? t('text.collapse') : t('text.expand')}>
                            {!expanded ? (
                                <Fade in>
                                    <ExpandMoreIcon />
                                </Fade>
                            ) : (
                                <ExpandMoreIcon />
                            )}
                        </Tooltip>
                    }
                >
                    <Badge
                        badgeContent={filter.length}
                        sx={{
                            ml: 2,
                            width: expanded ? 170 : 70,
                        }}
                        color="primary"
                    >
                        <Heading color="primary">{expanded ? t('text.filtration_rules') : t('text.filter_on')}</Heading>
                    </Badge>
                </AccordionSummary>

                <Details>
                    <ListGrid container spacing={2}>
                        {filter.map((item, index) => (
                            <Grid item xs={12} lg={6} key={item.code || index}>
                                <FilterViewMapper item={item} mindex={index} unique={filter} />
                            </Grid>
                        ))}
                    </ListGrid>
                </Details>

                <AccordionActions sx={{ justifyContent: 'flex-start' }}>
                    <Stack direction="column" spacing={1} ml={3} mb={2}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                flexDirection: 'row',
                            }}
                        >
                            {model
                                .slice(1, 99)
                                .filter(d => !hiddenCodes.includes(d.code))
                                .map((data, index) => {
                                    const activeFilter = filter.some(f => f.code === data.code);

                                    if (data.filter === false) return null;

                                    return (
                                        <Chip
                                            key={index}
                                            data-testid={`filter-${data.code}`}
                                            label={t(data.label)}
                                            color={activeFilter ? 'primary' : 'default'}
                                            onClick={() => handleAddFilter(data)}
                                            onDelete={activeFilter ? () => handleAddFilter(data) : undefined}
                                            sx={{ mb: 1, mr: 1 }}
                                        />
                                    );
                                })}
                        </Box>

                        <Stack direction="row" spacing={1} ml={1} flexWrap="wrap">
                            <Button
                                className="filterButtons"
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={fetchOnFilter && !filter.length}
                            >
                                {t('buttons.apply')}
                            </Button>

                            <Button
                                className="filterButtons clear"
                                variant="contained"
                                color="secondary"
                                onClick={() => {
                                    onAccept([]);
                                    reset();
                                }}
                            >
                                {t('buttons.reset')}
                            </Button>
                        </Stack>
                    </Stack>
                </AccordionActions>
            </RootAccordion>
        </Form>
    );
}

/* ================= HOCs ================= */

const withForm = reduxForm({
    form: FILTER_FORM_NAME,
    keepDirtyOnReinitialize: true,
    updateUnregisteredFields: true,
    onSubmit,
    validate,
});

export default withTranslation()(withForm(View));
